import { defineStore } from 'pinia';
import { adminApi } from '@/api/admin';
import type { EnvironmentDto } from '@/api/types';

// Single source of truth for the registered Environments. The list is small (a handful of
// entries), rarely changes, and is referenced by every Slot picker. Server order is canonical
// — the operator sets the order via the SPA's reorder UI, and every consumer (slot pickers,
// breadcrumbs, env drill-downs) honours it without any client-side sorting.
interface EnvironmentsState {
  list: EnvironmentDto[];
  loaded: boolean;
  loading: boolean;
  error: string | null;
}

export const useEnvironmentsStore = defineStore('environments', {
  state: (): EnvironmentsState => ({
    list: [],
    loaded: false,
    loading: false,
    error: null,
  }),
  getters: {
    // Slot dropdown choices: '_base' first, then registered envs in registry order.
    slotChoices: (state): string[] => ['_base', ...state.list.map((e) => e.name)],
  },
  actions: {
    async load(force = false) {
      if (this.loaded && !force) return;
      this.loading = true;
      this.error = null;
      try {
        this.list = await adminApi.listEnvironments();
        this.loaded = true;
      } catch (e) {
        this.error = (e as Error).message;
      } finally {
        this.loading = false;
      }
    },
    async add(name: string): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.addEnvironment({ name });
        await this.load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    async remove(name: string): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.removeEnvironment(name);
        await this.load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    // Optimistic reorder: mutate the local list immediately, fire the PUT, and revert
    // on failure. The names list must exactly match the current set (server-validated).
    async reorder(names: string[]): Promise<{ ok: true } | { ok: false; error: string }> {
      const previous = this.list.slice();
      // Reorder the local list to match `names` order, preserving DTO shape.
      const byName = new Map(previous.map((e) => [e.name, e]));
      this.list = names.map((n) => byName.get(n)).filter((e): e is EnvironmentDto => e !== undefined);
      try {
        await adminApi.reorderEnvironments({ names });
        return { ok: true };
      } catch (e) {
        // Roll back and surface the error so the view can show feedback.
        this.list = previous;
        return { ok: false, error: (e as Error).message };
      }
    },
  },
});
